import { Controller } from "@hotwired/stimulus"

import {
  createTrailerRecordingState,
  transitionTrailerRecording
} from "../podcast_publishing/trailer_recording_state.mjs"
import { TrailerRecordingStore } from "../podcast_publishing/trailer_recording_store.mjs"
import {
  preferredTrailerRecordingMimeType,
  trailerRecordingSupported
} from "../podcast_publishing/trailer_recording_capabilities.mjs"

const MAX_SECONDS = 90
const MIN_SECONDS = 10
const LOCAL_RETENTION_MILLISECONDS = 7 * 24 * 60 * 60 * 1000

export default class extends Controller {
  static targets = [
    "countdown",
    "device",
    "deviceField",
    "discardButton",
    "fileUploadButton",
    "meter",
    "preview",
    "progress",
    "recovery",
    "startButton",
    "status",
    "stopButton",
    "timer",
    "uploadButton",
    "continueAction"
  ]

  static values = {
    continuePrimary: Boolean,
    fileUploadPrimary: Boolean,
    recordingId: String,
    successUrl: String,
    uploadUrl: String
  }

  connect() {
    this.state = createTrailerRecordingState()
    this.currentBlob = null
    this.currentMimeType = "audio/webm"
    this.mediaRecorder = null
    this.mediaStream = null
    this.memoryChunks = []
    this.chunkIndex = 0
    this.chunkWrites = Promise.resolve()
    this.previewUrl = null
    this.recordingStartedAt = null
    this.boundPageHide = () => this.stopForPageHide()
    this.store = window.indexedDB
      ? new TrailerRecordingStore({ indexedDB: window.indexedDB })
      : null

    window.addEventListener("pagehide", this.boundPageHide)
    this.render()
    this.restoreLocalTake()
  }

  disconnect() {
    window.removeEventListener("pagehide", this.boundPageHide)
    this.clearCountdown()
    this.clearRecordingTimer()
    this.stopMeter()
    this.stopTracks()
    this.releaseWakeLock()
    this.revokePreview()
    const store = this.store
    this.chunkWrites.finally(() => store?.close())
  }

  async start() {
    if (!this.recordingSupported()) {
      this.failMicrophone("Recording is not supported here. Upload an audio file instead.")
      return
    }

    await this.requestPersistentStorage()
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraint() })
      await this.updateDevices().catch(() => {})
      this.state = transitionTrailerRecording(this.state, { type: "record_requested" })
      this.render()
      this.startMeter()
      this.countdownTimer = window.setInterval(() => this.advanceCountdown(), 1000)
    } catch (_error) {
      this.stopTracks()
      this.failMicrophone("Microphone access failed. Allow access or upload an audio file instead.")
    }
  }

  stop() {
    if (!this.mediaRecorder || this.mediaRecorder.state !== "recording") return

    this.state = transitionTrailerRecording(this.state, { type: "stop_requested" })
    this.render()
    this.mediaRecorder.stop()
  }

  async discard() {
    if (this.state.phase === "countdown") {
      this.clearCountdown()
      this.stopMeter()
      this.stopTracks()
    }
    if (this.mediaRecorder?.state === "recording") return

    try {
      await this.store?.discard(this.recordingIdValue)
    } catch (_error) {
      this.store = null
    }
    this.currentBlob = null
    this.memoryChunks = []
    this.revokePreview()
    this.state = transitionTrailerRecording(this.state, { type: "discarded" })
    this.render()
  }

  upload() {
    if (!this.currentBlob || this.state.durationSeconds < MIN_SECONDS) return

    this.state = transitionTrailerRecording(this.state, { type: "upload_started" })
    this.render()
    const request = new XMLHttpRequest()
    const formData = new FormData()
    formData.append("trailer[source_audio]", this.currentBlob, this.recordingFilename())
    request.open("POST", this.uploadUrlValue)
    request.setRequestHeader("Accept", "application/json")
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) request.setRequestHeader("X-CSRF-Token", csrfToken)

    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return
      this.progressTarget.value = Math.round((event.loaded / event.total) * 100)
    })
    request.addEventListener("load", () => this.finishUpload(request))
    request.addEventListener("error", () => this.failUpload("Connection lost. Your recording is still on this device."))
    request.addEventListener("abort", () => this.failUpload("Upload stopped. Your recording is still on this device."))
    request.send(formData)
  }

  async advanceCountdown() {
    this.state = transitionTrailerRecording(this.state, { type: "countdown_tick" })
    this.render()
    if (this.state.phase !== "recording") return

    this.clearCountdown()
    try {
      await this.beginRecording()
    } catch (_error) {
      this.stopTracks()
      this.failMicrophone("Recording could not start. Upload an audio file or try again.")
    }
  }

  async beginRecording() {
    this.currentMimeType = this.preferredMimeType()
    this.mediaRecorder = this.currentMimeType
      ? new MediaRecorder(this.mediaStream, { mimeType: this.currentMimeType })
      : new MediaRecorder(this.mediaStream)
    this.currentMimeType = this.mediaRecorder.mimeType || this.currentMimeType || "audio/webm"
    await this.prepareLocalTake(this.currentMimeType)
    this.currentBlob = null
    this.state = transitionTrailerRecording(this.state, { type: "capture_started" })
    this.recordingStartedAt = performance.now()
    this.memoryChunks = []
    this.chunkIndex = 0
    this.chunkWrites = Promise.resolve()
    this.revokePreview()

    this.mediaRecorder.addEventListener("dataavailable", (event) => this.saveChunk(event.data))
    this.mediaRecorder.addEventListener("stop", () => this.finalizeRecording(), { once: true })
    this.mediaRecorder.addEventListener("error", () => {
      this.failMicrophone("Recording stopped unexpectedly. Recover this take or try again.")
    }, { once: true })
    this.mediaRecorder.start(1000)
    this.recordingTimer = window.setInterval(() => this.updateElapsedTime(), 250)
    this.acquireWakeLock()
    this.render()
  }

  async prepareLocalTake(mimeType) {
    if (!this.store) return

    try {
      await this.store.discard(this.recordingIdValue)
      await this.store.start({
        id: this.recordingIdValue,
        mimeType,
        startedAt: Date.now(),
        expiresAt: Date.now() + LOCAL_RETENTION_MILLISECONDS
      })
    } catch (_error) {
      this.store = null
    }
  }

  saveChunk(chunk) {
    if (!(chunk instanceof Blob) || chunk.size === 0) return

    const index = this.chunkIndex
    this.chunkIndex += 1
    this.memoryChunks.push(chunk)
    if (!this.store) return

    this.chunkWrites = this.chunkWrites
      .then(() => this.store?.appendChunk(
        this.recordingIdValue,
        index,
        chunk,
        { durationMilliseconds: performance.now() - this.recordingStartedAt }
      ))
      .catch(() => {
        this.store = null
      })
  }

  updateElapsedTime() {
    if (!this.recordingStartedAt || this.state.phase !== "recording") return

    const elapsedMilliseconds = performance.now() - this.recordingStartedAt
    const seconds = elapsedMilliseconds >= 89_500 ? MAX_SECONDS : Math.floor(elapsedMilliseconds / 1000)
    this.state = transitionTrailerRecording(this.state, { type: "elapsed", seconds })
    this.render()
    if (this.state.stopRequested && this.mediaRecorder?.state === "recording") this.mediaRecorder.stop()
  }

  async finalizeRecording() {
    this.clearRecordingTimer()
    this.stopMeter()
    this.stopTracks()
    this.releaseWakeLock()
    await this.chunkWrites
    const durationMilliseconds = Math.min(
      Math.round(performance.now() - (this.recordingStartedAt || performance.now())),
      MAX_SECONDS * 1000
    )

    let storedTake = null
    if (this.store) {
      try {
        await this.store.markReady(this.recordingIdValue, { durationMilliseconds })
        storedTake = await this.store.find(this.recordingIdValue)
      } catch (_error) {
        this.store = null
      }
    }

    this.currentBlob = storedTake?.blob || new Blob(this.memoryChunks, { type: this.currentMimeType })
    this.state = transitionTrailerRecording(this.state, {
      type: "recording_ready",
      seconds: durationMilliseconds / 1000
    })
    this.showPreview(this.currentBlob)
    this.mediaRecorder = null
    this.render()
  }

  async restoreLocalTake() {
    if (!this.store) return

    try {
      await this.store.deleteExpired(Date.now())
      const take = await this.store.find(this.recordingIdValue)
      if (!take || take.blob.size === 0) return

      this.currentBlob = take.blob
      this.currentMimeType = take.mimeType || take.blob.type || "audio/webm"
      this.state = transitionTrailerRecording(this.state, {
        type: "recovery_found",
        seconds: Number(take.durationMilliseconds) / 1000
      })
      this.showPreview(take.blob)
      this.render()
    } catch (_error) {
      this.store = null
    }
  }

  async finishUpload(request) {
    let payload = {}
    try {
      payload = JSON.parse(request.responseText || "{}")
    } catch (_error) {
      payload = {}
    }

    const sourceAudioConfirmed = payload.success === true && payload.trailer?.source_audio_ready === true
    if (request.status < 200 || request.status >= 300 || !sourceAudioConfirmed) {
      const message = payload.error?.message || payload.error || "Upload failed. Your recording is still on this device."
      this.failUpload(message)
      return
    }

    try {
      await this.store?.discard(this.recordingIdValue)
    } catch (_error) {
      this.store = null
    }
    this.currentBlob = null
    this.revokePreview()
    this.state = transitionTrailerRecording(this.state, { type: "upload_succeeded" })
    this.render()
    window.setTimeout(() => window.location.assign(this.successUrlValue), 400)
  }

  failUpload(message) {
    this.state = transitionTrailerRecording(this.state, { type: "upload_failed", message })
    this.render()
  }

  failMicrophone(message) {
    this.clearCountdown()
    this.clearRecordingTimer()
    this.stopMeter()
    this.stopTracks()
    this.releaseWakeLock()
    this.state = transitionTrailerRecording(this.state, { type: "device_failed", message })
    this.render()
  }

  render() {
    const phase = this.state.phase
    const busy = [ "countdown", "recording", "finalizing", "uploading" ].includes(phase)
    const canUseTake = this.state.hasRecording && !busy
    const canSubmitTake = canUseTake && this.state.durationSeconds >= MIN_SECONDS
    const fileUploadIsPrimary = !canSubmitTake && this.fileUploadPrimaryValue
    const continueIsPrimary = !canSubmitTake && this.continuePrimaryValue
    const recordIsPrimary = !canSubmitTake && !fileUploadIsPrimary && !continueIsPrimary
    this.startButtonTarget.disabled = busy
    this.startButtonTarget.textContent = this.state.hasRecording ? "Record again" : "Record my trailer"
    this.togglePrimary(this.startButtonTarget, recordIsPrimary)
    this.stopButtonTarget.disabled = phase !== "recording"
    this.discardButtonTarget.disabled = !canUseTake && phase !== "countdown"
    this.uploadButtonTarget.disabled = !canSubmitTake
    this.togglePrimary(this.uploadButtonTarget, canSubmitTake)
    if (this.hasFileUploadButtonTarget) this.togglePrimary(this.fileUploadButtonTarget, fileUploadIsPrimary)
    if (this.hasContinueActionTarget) this.togglePrimary(this.continueActionTarget, continueIsPrimary)
    this.countdownTarget.hidden = phase !== "countdown"
    this.countdownTarget.textContent = phase === "countdown" ? String(this.state.countdownSeconds) : ""
    this.recoveryTarget.hidden = !this.state.recovered
    this.progressTarget.hidden = phase !== "uploading"
    this.timerTarget.textContent = `${this.formatTime(this.state.elapsedSeconds || this.state.durationSeconds)} / 1:30`
    this.statusTarget.textContent = this.state.error || this.statusMessage()
  }

  togglePrimary(element, primary) {
    element.classList.toggle("fp-btn", primary)
    element.classList.toggle("fp-secondary", !primary)
  }

  statusMessage() {
    switch (this.state.phase) {
      case "countdown": return "Get ready."
      case "recording": return "Recording your Trailer."
      case "finalizing": return "Preparing your take."
      case "recoverable": return "Recovered on this device. Play it, upload it, or record again."
      case "ready":
        return this.state.durationSeconds < MIN_SECONDS
          ? "Record at least 10 seconds before upload."
          : "Your take is ready. Play it before upload."
      case "uploading": return "Uploading. Keep this screen open."
      case "uploaded": return "Your Trailer is stored privately."
      case "failed": return "Use the existing-audio option or try again."
      default: return "Ready to record."
    }
  }

  recordingSupported() {
    return trailerRecordingSupported({
      MediaRecorderClass: window.MediaRecorder,
      mediaDevices: navigator.mediaDevices
    })
  }

  async requestPersistentStorage() {
    try {
      await navigator.storage?.persist?.()
    } catch (_error) {
      // IndexedDB recovery still works while the origin remains available.
    }
  }

  preferredMimeType() {
    return preferredTrailerRecordingMimeType(window.MediaRecorder)
  }

  audioConstraint() {
    const deviceId = this.hasDeviceTarget ? this.deviceTarget.value : ""
    return deviceId ? { deviceId: { exact: deviceId } } : true
  }

  async updateDevices() {
    if (typeof navigator.mediaDevices?.enumerateDevices !== "function") return
    const devices = (await navigator.mediaDevices.enumerateDevices())
      .filter((device) => device.kind === "audioinput")
    if (devices.length < 2) return

    const selectedId = this.deviceTarget.value
    this.deviceTarget.replaceChildren(...devices.map((device, index) => {
      const option = document.createElement("option")
      option.value = device.deviceId
      option.textContent = device.label || `Microphone ${index + 1}`
      option.selected = device.deviceId === selectedId
      return option
    }))
    this.deviceFieldTarget.hidden = false
  }

  startMeter() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass || !this.mediaStream) return

    this.audioContext = new AudioContextClass()
    this.analyser = this.audioContext.createAnalyser()
    this.analyser.fftSize = 256
    this.audioContext.createMediaStreamSource(this.mediaStream).connect(this.analyser)
    this.meterData = new Uint8Array(this.analyser.frequencyBinCount)
    const draw = () => {
      if (!this.analyser) return
      this.analyser.getByteTimeDomainData(this.meterData)
      const peak = this.meterData.reduce((maximum, sample) => {
        return Math.max(maximum, Math.abs(sample - 128) / 128)
      }, 0)
      this.meterTarget.value = peak
      this.meterFrame = window.requestAnimationFrame(draw)
    }
    draw()
  }

  stopMeter() {
    if (this.meterFrame) window.cancelAnimationFrame(this.meterFrame)
    this.meterFrame = null
    this.analyser = null
    this.meterTarget.value = 0
    this.audioContext?.close().catch(() => {})
    this.audioContext = null
  }

  showPreview(blob) {
    this.revokePreview()
    this.previewUrl = URL.createObjectURL(blob)
    this.previewTarget.src = this.previewUrl
    this.previewTarget.hidden = false
  }

  revokePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
    this.previewUrl = null
    if (this.hasPreviewTarget) {
      this.previewTarget.removeAttribute("src")
      this.previewTarget.hidden = true
    }
  }

  recordingFilename() {
    let extension = "webm"
    if (this.currentMimeType.includes("mp4")) extension = "m4a"
    if (this.currentMimeType.includes("ogg")) extension = "ogg"
    return `trailer-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`
  }

  formatTime(seconds) {
    const wholeSeconds = Math.max(0, Math.min(Math.floor(Number(seconds) || 0), MAX_SECONDS))
    return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`
  }

  clearCountdown() {
    if (this.countdownTimer) window.clearInterval(this.countdownTimer)
    this.countdownTimer = null
  }

  clearRecordingTimer() {
    if (this.recordingTimer) window.clearInterval(this.recordingTimer)
    this.recordingTimer = null
  }

  stopTracks() {
    this.mediaStream?.getTracks().forEach((track) => track.stop())
    this.mediaStream = null
  }

  stopForPageHide() {
    if (this.mediaRecorder?.state !== "recording") return
    this.mediaRecorder.requestData()
    this.mediaRecorder.stop()
  }

  async acquireWakeLock() {
    try {
      this.wakeLock = await navigator.wakeLock?.request("screen")
    } catch (_error) {
      this.wakeLock = null
    }
  }

  releaseWakeLock() {
    this.wakeLock?.release().catch(() => {})
    this.wakeLock = null
  }
}
